/**
 * ContextMeter — how full the conversation is against the model's window.
 *
 * A composer chip, the way Claude shows its context: a small ring with the
 * percentage at the composer's trailing edge. Absent entirely under 50%
 * (quiet), plain from 50%, amber from 75%, red from 90%. Clicking it opens a
 * compact popover — "Context 63% — compacts automatically at 80%" — with a
 * small "Compact now" text button. The reading comes from the `context_usage`
 * SSE events and `GET …/context-usage`; a model switch re-bands it (the
 * window changes).
 *
 * Styled with the composer's chip tokens (`.mode-menu-trigger`: agent-soft
 * pill, 11.5px/600). Shared by the regular chat and the Agent of Agents
 * page through ChatInput's `contextMeter` slot. RTL-safe: no physical
 * margins, numbers formatted by the active locale.
 */

import { useState } from 'react';
import { Button, Popover } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale } from '@/utils/locale';
import type { ContextUsage } from '@/types/models';
import './ContextMeter.css';

export interface ContextMeterProps {
  usage: ContextUsage | null | undefined;
  /** "Compact now" — absent when the viewer cannot write to the conversation. */
  onCompactNow?: () => Promise<void> | void;
  /** True while a compaction (manual or automatic) is running. */
  compacting?: boolean;
  className?: string;
}

/** Colour band for a ratio: quiet < 0.5 ≤ normal < 0.75 ≤ amber < 0.9 ≤ red. */
export function meterBand(ratio: number | null | undefined): 'quiet' | 'normal' | 'amber' | 'red' {
  const r = ratio ?? 0;
  if (r >= 0.9) return 'red';
  if (r >= 0.75) return 'amber';
  if (r >= 0.5) return 'normal';
  return 'quiet';
}

/** 142300 → "142k"; below a thousand the plain number. Locale-aware digits. */
export function formatTokens(n: number, locale: string): string {
  if (!Number.isFinite(n)) return '0';
  if (n < 1000) return new Intl.NumberFormat(locale).format(Math.round(n));
  const k = n / 1000;
  const shown = k >= 100 ? Math.round(k) : Math.round(k * 10) / 10;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(shown)}k`;
}

const RADIUS = 6;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function ContextMeter({ usage, onCompactNow, compacting = false, className }: ContextMeterProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!usage || !usage.window) return null;

  const locale = activeLocale();
  const ratio = Math.max(0, Math.min(1, usage.ratio ?? usage.used / usage.window));
  const band = meterBand(ratio);
  const running = compacting || busy;
  // Quiet under 50%: nothing on screen, unless a compaction is running and
  // the spinner is the only sign of it.
  if (band === 'quiet' && !running) return null;

  const percent = Math.round(ratio * 100);
  const percentLabel = new Intl.NumberFormat(locale).format(percent);
  const compactAtLabel = new Intl.NumberFormat(locale).format(Math.round((usage.compact_at_ratio ?? 0.8) * 100));
  const usedLabel = formatTokens(usage.used, locale);
  const windowLabel = formatTokens(usage.window, locale);

  const line = usage.auto_compact_enabled === false
    ? t('chat.context.lineNoAuto', { percent: percentLabel })
    : t('chat.context.line', { percent: percentLabel, compactAt: compactAtLabel });
  const detail = t('chat.context.usedOf', { used: usedLabel, window: windowLabel })
    + (usage.estimated ? ` ${t('chat.context.estimated')}` : '');

  const handleCompact = async () => {
    if (!onCompactNow || running) return;
    setBusy(true);
    try {
      await onCompactNow();
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const content = (
    <div className="context-meter-popover" dir="auto">
      <div className="context-meter-popover-line">{line}</div>
      <div className="context-meter-popover-detail">{detail}</div>
      {onCompactNow && (
        <Button
          size="small"
          type="link"
          loading={running}
          onClick={handleCompact}
          data-testid="context-compact-now"
          className="context-meter-compact-btn"
        >
          {running ? t('chat.context.compacting') : t('chat.context.compactNow')}
        </Button>
      )}
    </div>
  );

  const dash = CIRCUMFERENCE * (1 - ratio);

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="top"
      overlayClassName="context-meter-overlay"
    >
      <button
        type="button"
        className={`context-meter context-meter--${band}${running ? ' context-meter--busy' : ''}${className ? ` ${className}` : ''}`}
        title={line}
        aria-label={line}
        data-testid="context-meter"
        data-band={band}
        data-percent={percent}
      >
        <svg className="context-meter-ring" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <circle className="context-meter-track" cx="8" cy="8" r={RADIUS} />
          <circle
            className="context-meter-fill"
            cx="8"
            cy="8"
            r={RADIUS}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dash}
          />
        </svg>
        <span className="context-meter-percent">{percentLabel}%</span>
      </button>
    </Popover>
  );
}

export default ContextMeter;
